import type { KeyboardEvent, ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useCallback, useMemo, useState } from 'react'
import {
  Activity,
  Github,
  History,
  Home,
  Menu,
  Mic2,
  Music,
  Sliders,
  Sparkles,
  X,
} from 'lucide-react'
import { useAppStore } from '../stores/appStore'
import { useSessionData } from '../hooks/useSessionData'
import { usePWA } from '../hooks/usePWA'

interface LayoutProps {
  children: ReactNode
}

const navItems = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/stems', label: 'Stems', icon: Music },
  { path: '/mastering', label: 'Mastering', icon: Sliders },
  { path: '/analysis', label: 'Analysis', icon: Activity },
  { path: '/voice', label: 'Voice', icon: Mic2 },
  { path: '/history', label: 'History', icon: History },
] as const

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const jobs = useAppStore((state) => state.jobs)
  const { isLoading, error } = useSessionData()
  const { needRefresh, updateApp } = usePWA()

  const activeJobCount = useMemo(
    () => jobs.filter((job) => job.status === 'processing' || job.status === 'pending').length,
    [jobs]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>, path: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        navigate(path)
      }
    },
    [navigate]
  )

  return (
    <div className="min-h-screen studio-shell">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 z-50 px-4 py-2 rounded-md bg-zinc-100 text-zinc-900"
      >
        Skip to content
      </a>

      <header className="studio-header">
        <div className="studio-header-inner">
          <Link to="/" className="logo-block">
            <div className="logo-badge">
              <Sparkles className="w-4 h-4" />
            </div>
            <div>
              <p className="logo-title">AI Producer</p>
              <p className="logo-copy">Cloud Studio</p>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={`nav-link ${isActive ? 'nav-link-active' : ''}`}
                  onKeyDown={(event) => handleKeyDown(event, item.path)}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              )
            })}
          </nav>

          <div className="flex items-center gap-3">
            <div className="studio-chip hidden sm:flex">
              <span className={activeJobCount > 0 ? 'pulse-dot' : 'pulse-dot idle'} />
              {isLoading
                ? 'Syncing session...'
                : activeJobCount > 0
                ? `${activeJobCount} active job${activeJobCount > 1 ? 's' : ''}`
                : error
                ? 'Session sync issue'
                : 'No active jobs'}
            </div>
            <a
              href="https://github.com"
              target="_blank"
              rel="noreferrer"
              className="icon-button"
              aria-label="GitHub"
            >
              <Github className="w-4 h-4" />
            </a>
            <button
              onClick={() => setMobileMenuOpen((value) => !value)}
              className="icon-button lg:hidden"
              aria-label="Toggle menu"
              type="button"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen ? (
          <div className="lg:hidden studio-mobile-nav">
            {navItems.map((item) => {
              const Icon = item.icon
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`mobile-link ${isActive ? 'mobile-link-active' : ''}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </Link>
              )
            })}
          </div>
        ) : null}
      </header>

      <main id="main-content" className="studio-main">
        {needRefresh && updateApp ? (
          <div className="mb-4 rounded-xl border border-brand-cyan/30 bg-brand-cyan/10 p-3 text-sm text-zinc-100">
            A new studio version is available.
            <button type="button" className="ml-3 studio-chip" onClick={updateApp}>
              Update
            </button>
          </div>
        ) : null}
        {children}
      </main>

      <footer className="studio-footer">
        <div className="studio-footer-inner">
          <p>AI Producer • dark studio workflow for mixed creator/pro sessions.</p>
          <p>Realtime jobs • cloud-ready deployment</p>
        </div>
      </footer>
    </div>
  )
}
